import React, { ReactElement, useState, useEffect } from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    MenuItem,
    Select,
    Tab,
    Tabs,
    Typography,
    alpha,
    Chip,
    CircularProgress,
} from '@mui/material';
import { Close, CheckCircle, Warning, Image as ImageIcon, RocketLaunch } from '@mui/icons-material';
import { confirmProfilePhotoImport } from 'api';
import { pushMessage } from 'redux/reducers/messages';
import { useAppDispatch } from 'redux/store';
import { Group } from 'utils';
import useTheme from '@mui/system/useTheme';

export interface PhotoMatch {
    filename: string;
    member_uuid: string;
    member_name: string;
    temp_path: string;
    thumbnail_url?: string;
    confidence: string;
}

export interface MultipleMatch {
    filename: string;
    temp_path: string;
    thumbnail_url?: string;
    potential_matches: Array<{
        member_uuid: string;
        member_name: string;
        confidence: string;
    }>;
}

export interface PhotoMatchResult {
    status: string;
    upload_token: string;
    summary: {
        total_photos: number;
        matched: number;
        unmatched: number;
        multiple_matches: number;
        invalid_files?: number;
    };
    matches: PhotoMatch[];
    unmatched_photos: string[];
    multiple_matches: MultipleMatch[];
    invalid_files?: string[];
}

interface ProfilePhotoMatchReviewProps {
    open: boolean;
    matchResult: PhotoMatchResult;
    group: Group;
    onClose: () => void;
    onSuccess: () => void;
}

export default function ProfilePhotoMatchReview({
    open,
    matchResult,
    group,
    onClose,
    onSuccess,
}: ProfilePhotoMatchReviewProps): ReactElement {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(0);
    const [manualMatches, setManualMatches] = useState<Record<string, string>>({});
    const [confirming, setConfirming] = useState(false);
    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

    const loadingMessages = [
        'Adding photos to deport missile',
        'Aiming...',
        'In Transit...',
        'Looking for lost deport vehicle...',
        'Confirmation received',
        'Decoding super encoder',
        'Almost done...',
    ];

    // Cycle through loading messages continuously
    useEffect(() => {
        if (!confirming) {
            setLoadingMessageIndex(0);
            return;
        }

        const interval = setInterval(() => {
            setLoadingMessageIndex((prev) => {
                // Loop back to start after reaching the end
                return (prev + 1) % loadingMessages.length;
            });
        }, 1500); // Change message every 1.5 seconds

        return () => clearInterval(interval);
    }, [confirming, loadingMessages.length]);

    // Component mount logging
    React.useEffect(() => {
        console.log('=== ProfilePhotoMatchReview MOUNTED ===');
        console.log('matchResult prop:', matchResult);
        console.log('matchResult.matches length:', matchResult?.matches?.length);
        console.log('matchResult.multiple_matches length:', matchResult?.multiple_matches?.length);

        if (matchResult?.matches && matchResult.matches.length > 0) {
            console.log('First match from props:', matchResult.matches[0]);
            console.log(
                'Props - First match has thumbnail_url:',
                'thumbnail_url' in matchResult.matches[0],
            );
            console.log(
                'Props - First match thumbnail_url value:',
                matchResult.matches[0].thumbnail_url,
            );
        }

        if (matchResult?.multiple_matches && matchResult.multiple_matches.length > 0) {
            console.log('First multiple match from props:', matchResult.multiple_matches[0]);
            console.log(
                'Props - First multiple match has thumbnail_url:',
                'thumbnail_url' in matchResult.multiple_matches[0],
            );
        }
        console.log('=== END COMPONENT MOUNT LOG ===');
    }, [matchResult]);

    const glassCardStyle = {
        background: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(12px)',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
    };

    const handleManualMatch = (filename: string, memberUuid: string) => {
        setManualMatches((prev) => ({
            ...prev,
            [filename]: memberUuid,
        }));
    };

    const handleConfirm = async () => {
        setConfirming(true);
        try {
            const manualMatchesArray = Object.entries(manualMatches).map(
                ([filename, member_uuid]) => ({
                    filename,
                    member_uuid,
                }),
            );

            await confirmProfilePhotoImport(group, {
                upload_token: matchResult.upload_token,
                action: 'apply',
                manual_matches: manualMatchesArray,
            });

            dispatch(
                pushMessage({
                    status: 'success',
                    message: `Successfully uploaded ${
                        matchResult.summary.matched + manualMatchesArray.length
                    } profile photos to ${group.name}`,
                }),
            );
            onSuccess();
        } catch (error: any) {
            console.error('Confirm error:', error);
            dispatch(
                pushMessage({
                    status: 'error',
                    message:
                        error?.response?.data?.message ||
                        'Failed to apply photos. Please try again.',
                }),
            );
        } finally {
            setConfirming(false);
        }
    };

    const handleCancel = async () => {
        try {
            await confirmProfilePhotoImport(group, {
                upload_token: matchResult.upload_token,
                action: 'cancel',
            });
            onClose();
        } catch (error) {
            console.error('Cancel error:', error);
            onClose();
        }
    };

    const totalToApply = matchResult.summary.matched + Object.keys(manualMatches).length;

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Review Photo Matches</Typography>
                        <IconButton onClick={handleCancel} disabled={confirming}>
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ py: 2 }}>
                        {/* Summary Card */}
                        <Card sx={{ ...glassCardStyle, p: 3, mb: 3 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={3}>
                                    <Box textAlign="center">
                                        <Typography variant="h4" color="primary">
                                            {matchResult.summary.matched}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Matched
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Box textAlign="center">
                                        <Typography variant="h4" color="warning.main">
                                            {matchResult.summary.unmatched}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Unmatched
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Box textAlign="center">
                                        <Typography variant="h4" color="info.main">
                                            {matchResult.summary.multiple_matches}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Multiple Matches
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Box textAlign="center">
                                        <Typography variant="h4" color="success.main">
                                            {totalToApply}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Will Be Applied
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Card>

                        {/* Tabs */}
                        <Tabs
                            value={activeTab}
                            onChange={(_, newValue) => setActiveTab(newValue)}
                            sx={{ mb: 2 }}
                        >
                            <Tab
                                label={`Matched (${matchResult.summary.matched})`}
                                icon={<CheckCircle fontSize="small" />}
                                iconPosition="start"
                            />
                            {matchResult.summary.multiple_matches > 0 && (
                                <Tab
                                    label={`Multiple (${matchResult.summary.multiple_matches})`}
                                    icon={<Warning fontSize="small" />}
                                    iconPosition="start"
                                />
                            )}
                            {matchResult.summary.unmatched > 0 && (
                                <Tab
                                    label={`Unmatched (${matchResult.summary.unmatched})`}
                                    icon={<Warning fontSize="small" />}
                                    iconPosition="start"
                                />
                            )}
                        </Tabs>

                        {/* Matched Photos Tab */}
                        {activeTab === 0 && (
                            <Box sx={{ ...glassCardStyle, p: 2, maxHeight: 400, overflow: 'auto' }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mb: 2, display: 'block' }}
                                >
                                    DEBUG: Rendering {matchResult.matches.length} matched photos
                                </Typography>
                                <Grid container spacing={2}>
                                    {matchResult.matches.map((match, index) => {
                                        console.log(
                                            `RENDER: Match ${index + 1}/${
                                                matchResult.matches.length
                                            }:`,
                                            {
                                                filename: match.filename,
                                                member_name: match.member_name,
                                                has_thumbnail_url: !!match.thumbnail_url,
                                                thumbnail_url_length:
                                                    match.thumbnail_url?.length || 0,
                                                thumbnail_url_start: match.thumbnail_url?.substring(
                                                    0,
                                                    50,
                                                ),
                                                full_object: match,
                                            },
                                        );
                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={index}>
                                                <Card
                                                    sx={{
                                                        p: 2,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                    }}
                                                >
                                                    {match.thumbnail_url ? (
                                                        <Avatar
                                                            src={match.thumbnail_url}
                                                            variant="rounded"
                                                            sx={{
                                                                width: 60,
                                                                height: 60,
                                                                border: `2px solid ${theme.palette.success.main}`,
                                                            }}
                                                        />
                                                    ) : (
                                                        <ImageIcon
                                                            sx={{
                                                                fontSize: 60,
                                                                color: 'success.main',
                                                            }}
                                                        />
                                                    )}
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="subtitle2" noWrap>
                                                            {match.member_name}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            noWrap
                                                        >
                                                            {match.filename}
                                                        </Typography>
                                                        <Box sx={{ mt: 0.5 }}>
                                                            <Chip
                                                                label={match.confidence}
                                                                size="small"
                                                                color={
                                                                    match.confidence === 'high'
                                                                        ? 'success'
                                                                        : 'default'
                                                                }
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        )}

                        {/* Multiple Matches Tab */}
                        {activeTab === 1 && matchResult.summary.multiple_matches > 0 && (
                            <Box sx={{ ...glassCardStyle, p: 2, maxHeight: 400, overflow: 'auto' }}>
                                {matchResult.multiple_matches.map((multiMatch, index) => (
                                    <Card key={index} sx={{ p: 2, mb: 2 }}>
                                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                            {multiMatch.thumbnail_url ? (
                                                <Avatar
                                                    src={multiMatch.thumbnail_url}
                                                    variant="rounded"
                                                    sx={{
                                                        width: 80,
                                                        height: 80,
                                                        border: `2px solid ${theme.palette.warning.main}`,
                                                    }}
                                                />
                                            ) : (
                                                <ImageIcon
                                                    sx={{ fontSize: 80, color: 'warning.main' }}
                                                />
                                            )}
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    {multiMatch.filename}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    paragraph
                                                >
                                                    Multiple members match this photo. Please select
                                                    the correct one:
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={manualMatches[multiMatch.filename] || ''}
                                                onChange={(e) =>
                                                    handleManualMatch(
                                                        multiMatch.filename,
                                                        e.target.value,
                                                    )
                                                }
                                                displayEmpty
                                            >
                                                <MenuItem value="">
                                                    <em>Select a member...</em>
                                                </MenuItem>
                                                {multiMatch.potential_matches.map((pm, pmIndex) => (
                                                    <MenuItem key={pmIndex} value={pm.member_uuid}>
                                                        {pm.member_name} ({pm.confidence}{' '}
                                                        confidence)
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Card>
                                ))}
                            </Box>
                        )}

                        {/* Unmatched Photos Tab */}
                        {activeTab === (matchResult.summary.multiple_matches > 0 ? 2 : 1) &&
                            matchResult.summary.unmatched > 0 && (
                                <Box
                                    sx={{
                                        ...glassCardStyle,
                                        p: 2,
                                        maxHeight: 400,
                                        overflow: 'auto',
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary" paragraph>
                                        These photos could not be automatically matched to any
                                        members. They will be skipped.
                                    </Typography>
                                    <Grid container spacing={1}>
                                        {matchResult.unmatched_photos.map((filename, index) => (
                                            <Grid item xs={12} sm={6} md={4} key={index}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        p: 1,
                                                        borderRadius: 1,
                                                        bgcolor: alpha(
                                                            theme.palette.warning.main,
                                                            0.1,
                                                        ),
                                                    }}
                                                >
                                                    <ImageIcon
                                                        fontSize="small"
                                                        sx={{ color: 'warning.main' }}
                                                    />
                                                    <Typography variant="caption" noWrap>
                                                        {filename}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}

                        {/* Action Buttons */}
                        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button onClick={handleCancel} disabled={confirming}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleConfirm}
                                disabled={confirming || totalToApply === 0}
                                color="success"
                            >
                                {confirming
                                    ? 'Applying...'
                                    : `Apply ${totalToApply} Photo${totalToApply !== 1 ? 's' : ''}`}
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Loading Modal with Glass UI */}
            <Dialog
                open={confirming}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        background: `linear-gradient(135deg, ${alpha(
                            theme.palette.primary.main,
                            0.15,
                        )} 0%, ${alpha(theme.palette.secondary.main, 0.15)} 100%)`,
                        backdropFilter: 'blur(20px)',
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.4)}`,
                    },
                }}
            >
                <DialogContent>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 3,
                            py: 4,
                        }}
                    >
                        {/* Animated Icon */}
                        <Box
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CircularProgress
                                size={80}
                                thickness={2}
                                sx={{
                                    color: theme.palette.primary.main,
                                    opacity: 0.3,
                                }}
                            />
                            <RocketLaunch
                                sx={{
                                    position: 'absolute',
                                    fontSize: 40,
                                    color: theme.palette.primary.main,
                                    animation: 'pulse 1.5s ease-in-out infinite',
                                    '@keyframes pulse': {
                                        '0%, 100%': {
                                            transform: 'scale(1) rotate(0deg)',
                                        },
                                        '50%': {
                                            transform: 'scale(1.1) rotate(5deg)',
                                        },
                                    },
                                }}
                            />
                        </Box>

                        {/* Loading Message */}
                        <Box
                            sx={{
                                textAlign: 'center',
                                minHeight: 60,
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    animation: 'fadeInOut 1.5s ease-in-out infinite',
                                    '@keyframes fadeInOut': {
                                        '0%, 100%': {
                                            opacity: 1,
                                        },
                                        '50%': {
                                            opacity: 0.5,
                                        },
                                    },
                                }}
                            >
                                {loadingMessages[loadingMessageIndex]}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    mt: 1,
                                    display: 'block',
                                }}
                            >
                                Please wait while we process your photos...
                            </Typography>
                        </Box>

                        {/* Progress Indicator */}
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                gap: 0.5,
                                justifyContent: 'center',
                                px: 4,
                            }}
                        >
                            {loadingMessages.map((_, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        flex: 1,
                                        height: 4,
                                        borderRadius: 2,
                                        background:
                                            index <= loadingMessageIndex
                                                ? theme.palette.primary.main
                                                : alpha(theme.palette.divider, 0.3),
                                        transition: 'background 0.3s ease',
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}
